import React, { useState } from 'react';
import { toast } from 'react-toastify';

import '../../css/modal.css';
import { updateCustomer } from '../../api/Customer';
import { Customer, CustomerUpdate } from '../../types/Customer';
import CustomerFormFields from './CustomerFormFields';

interface CustomerEditModalProps {
  customer: Customer;
  onClose: () => void;
  onCustomerUpdated: (customer: Customer) => void;
}

const editableFields = (customer: Customer): CustomerUpdate => ({
  name: customer.name || '',
  email: customer.email || '',
  city: customer.city || '',
  address: customer.address || '',
  profession: customer.profession || '',
  source: customer.source,
  description: customer.description || '',
  area: customer.area || '',
});

const CustomerEditModal: React.FC<CustomerEditModalProps> = ({ customer, onClose, onCustomerUpdated }) => {
  const [formData, setFormData] = useState<CustomerUpdate>(() => editableFields(customer));
  const [saving, setSaving] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData(current => ({ ...current, [name]: value }));
  };

  const handleSubmit = async () => {
    const name = formData.name.trim();
    const email = formData.email?.trim() || '';

    if (!name) {
      toast.error('Customer name is required.');
      return;
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.warning('Enter a valid email address.');
      return;
    }

    if (!customer.id) {
      toast.error('This customer cannot be updated because its ID is missing.');
      return;
    }

    try {
      setSaving(true);
      const updatedCustomer = await updateCustomer(customer.id, { ...formData, name, email });
      // Keep immutable identity and captured-location fields from the original
      // record even if a malformed API response contains different values.
      onCustomerUpdated({
        ...customer,
        ...updatedCustomer,
        phoneNumber: customer.phoneNumber,
        latitude: customer.latitude,
        longitude: customer.longitude,
      });
      toast.success('Customer updated successfully!');
    } catch (error) {
      toast.error('Failed to update customer.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div className="modal modal-lg customer-modal" role="dialog" aria-modal="true" aria-labelledby="edit-customer-title" onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <h2 id="edit-customer-title">Edit Customer</h2>
          <button className="close-btn" onClick={onClose} aria-label="Close edit customer dialog">×</button>
        </div>

        <CustomerFormFields
          formData={formData}
          onChange={handleChange}
          phoneField={(
            <>
              <label htmlFor="customer-phone-number">Phone number (cannot be edited)</label>
              <input id="customer-phone-number" value={customer.phoneNumber} aria-label="Phone number" disabled title="Phone numbers cannot be edited here" />
            </>
          )}
        />

        <div className="modal-actions">
          <button className="secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
};

export default CustomerEditModal;
